import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLine, VictoryScatter } from 'victory';
import DashboardService from 'api/dashboard/DashboardService';
import { MonthlyCostDashboardDto } from 'api/dashboard/dtos/MonthlyCostDashboardDto';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import Utils from 'common/services/Utils';
import dayjs from 'dayjs';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './MonthlyExpensesEvolution.module.scss';

const BAR_COLOR = 'rgba(2, 199, 199, 0.3)';
const BAR_STROKE = '#02c7c7';
const TREND_COLOR = '#d4a832';

const MonthlyExpensesEvolution = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<MonthlyCostDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('monthlyExpensesEvolution');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());
	const [tooltip, setTooltip] = useState<{ label: string; value: number; trend: number | null; x: number; y: number } | null>(null);
	const [barsHidden, setBarsHidden] = useState(false);
	const [trendHidden, setTrendHidden] = useState(false);

	const toggleBars = () => {
		if (!barsHidden && trendHidden) return;
		setBarsHidden(v => !v);
	};
	const toggleTrend = () => {
		if (!trendHidden && barsHidden) return;
		setTrendHidden(v => !v);
	};

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const result = await DashboardService.getMonthlyCost({ dateFrom, dateTo, ...vehicleScope });
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [dateFrom, dateTo]);

	// Aggregate fixed + variable costs per month
	const monthlyTotals = useMemo(() => {
		if (!data?.items) return [];
		const dateMap = new Map<string, number>();
		data.items.forEach(typeItem => {
			typeItem.items.forEach(item => {
				const key = dayjs(item.date).format('YYYY-MM');
				dateMap.set(key, (dateMap.get(key) ?? 0) + item.value);
			});
		});
		return Array.from(dateMap.entries())
			.map(([key, value]) => ({ date: key, value }))
			.sort((a, b) => a.date.localeCompare(b.date));
	}, [data]);

	// 3-month moving average for trend line
	const trendData = useMemo(() => {
		if (monthlyTotals.length < 2) return [];
		return monthlyTotals.map((item, i) => {
			const windowSize = Math.min(3, i + 1);
			const sum = monthlyTotals.slice(Math.max(0, i - windowSize + 1), i + 1).reduce((s, m) => s + m.value, 0);
			return { date: item.date, value: sum / windowSize };
		});
	}, [monthlyTotals]);

	const periodTotal = monthlyTotals.reduce((sum, item) => sum + item.value, 0);

	const MAX_TICKS = 12;
	const TICK_STEPS = [1, 2, 3, 6, 12];
	const tickStep = TICK_STEPS.find(s => Math.ceil(monthlyTotals.length / s) <= MAX_TICKS) ?? 12;

	if (!data && !isLoading) return null;

	return (
		<Box className={tableStyles.container} title={t('dashboard.monthly_expenses_evolution') as string}
			subTitleTooltip={t('dashboard.monthly_expenses_evolution_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}>
			<KpiHeader
				value={Utils.formatCurrency(periodTotal)}
				subLabel={t('dashboard.period_total' as any) as string}
				currentTotal={periodTotal}
				previousTotal={null}
				previousRange={null}
			/>
			<div className={styles.chartContainer} ref={containerRef}>
				{isLoading ? <div className={styles.centerAlign}><LoadingSpinner /></div> :
					monthlyTotals.length === 0 ? <WidgetNoData /> :
						width > 0 && height > 0 && (
							<>
							<VictoryChart
								width={width}
								height={height}
								domainPadding={{ x: Math.max(30, 60 - monthlyTotals.length * 3) }}
								padding={{ top: 10, bottom: 50, left: 80, right: 20 }}
							>
								<VictoryAxis
									style={{
										axis: { stroke: 'transparent' },
										tickLabels: { fill: '#000000', userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
									}}
									tickFormat={(x, i) => i % tickStep === 0 ? dayjs(x + '-01').format('MMM YY') : ''}
								/>
								<VictoryAxis
									dependentAxis
									style={{
										axis: { stroke: 'transparent' },
										grid: { stroke: '#f5f5f5' },
										tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
									}}
									tickFormat={x => Utils.formatCurrency(x)}
								/>
								{!barsHidden && (
								<VictoryBar
									data={monthlyTotals}
									x="date"
									y="value"
									barWidth={Math.min(60, ((width - 100) / Math.max(monthlyTotals.length, 1)) * 0.6)}
									cornerRadius={{ top: 4 }}
									labels={() => ''}
									// eslint-disable-next-line
									events={[{
										target: 'data' as const,
										eventHandlers: {
											onMouseEnter: (evt: any, targetProps: any) => {
												const datum = targetProps.datum;
												const trendItem = trendData.find(td => td.date === datum.date);
												setTooltip({
													label: dayjs(datum.date + '-01').format('MMM YYYY'),
													value: datum.value,
													trend: trendItem?.value ?? null,
													x: evt.clientX,
													y: evt.clientY,
												});
												return [];
											},
											onMouseMove: (evt: any) => {
												setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
												return [];
											},
											onMouseLeave: () => { setTooltip(null); return []; },
										},
									}]}
									style={{
										data: {
											fill: BAR_COLOR,
											stroke: BAR_STROKE,
											strokeWidth: 1.5,
											cursor: 'pointer',
										},
										labels: { display: 'none' },
									}}
								/>
								)}
								{!trendHidden && trendData.length > 1 && (
									<VictoryLine
										data={trendData}
										x="date"
										y="value"
										style={{
											data: {
												stroke: TREND_COLOR,
												strokeWidth: 2.5,
											},
										}}
									/>
								)}
								{!trendHidden && trendData.length > 0 && (
									<VictoryScatter
										data={trendData}
										x="date"
										y="value"
										size={4}
										style={{
											data: {
												fill: TREND_COLOR,
												stroke: '#fff',
												strokeWidth: 1.5,
											},
										}}
									/>
								)}
							</VictoryChart>
							<div className={styles.htmlLegend}>
								<span
									className={`${styles.legendItem} ${barsHidden ? styles.legendItemHidden : ''}`}
									onClick={toggleBars}
								>
									<span className={styles.legendDot} style={{ backgroundColor: barsHidden ? '#d1d5db' : BAR_STROKE }} />
									<span className={styles.legendName}>{t('dashboard.monthly_expenses_bars' as any)}</span>
								</span>
								<span
									className={`${styles.legendItem} ${trendHidden ? styles.legendItemHidden : ''}`}
									onClick={toggleTrend}
								>
									<span className={styles.legendDot} style={{ backgroundColor: trendHidden ? '#d1d5db' : TREND_COLOR }} />
									<span className={styles.legendName}>{t('dashboard.monthly_expenses_trend' as any)}</span>
								</span>
							</div>
							</>
						)}
				{tooltip && (
					<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
						<div className={styles.tooltipTitle}>{tooltip.label}</div>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: BAR_STROKE }} />
							<span className={styles.tooltipRowLabel}>{t('dashboard.monthly_expenses_bars')}:</span>
							<span className={styles.tooltipRowValue}>{Utils.formatCurrency(tooltip.value)}</span>
						</div>
						{tooltip.trend !== null && (
							<div className={styles.tooltipRow}>
								<span className={styles.tooltipDot} style={{ backgroundColor: TREND_COLOR }} />
								<span className={styles.tooltipRowLabel}>{t('dashboard.monthly_expenses_trend')}:</span>
								<span className={styles.tooltipRowValue}>{Utils.formatCurrency(tooltip.trend)}</span>
							</div>
						)}
					</ChartTooltipPortal>
				)}
			</div>
		</Box>
	);
};

export default MonthlyExpensesEvolution;
